import React, {useEffect, useState} from "react";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import moment from "moment/moment";
import {useSelector} from "react-redux";
import axios from "axios";
import {toast} from "react-toastify";
import {Badge, Col, Row, Spinner} from "react-bootstrap";
import DateHelper from "../../../../primer/DateHelper/DateHelper";
import DateBox from "../../../../primer/DateBox";
import BetterCheckBox from "../../../../common/BetterCheckBox";
import {textToLines} from "../constants";
import {useTranslation} from "../../../../../hooks/useTranslation";

const CopyPrescriptionModal = ({show, setShow, prescriptionCallback, amka}) => {
    const {t} = useTranslation();
    const company = useSelector((state) => state.COMPANY_DATA.company);

    const [loading, setLoading] = useState(false);
    const [requestData, setRequestData] = useState({
        date_from: moment().subtract(1, "month").format("DD/MM/YYYY"),
        date_to: moment().format("DD/MM/YYYY"),
        page: 1,
        patientsConsent: false,
    });
    const [totalPages, setTotalPages] = useState(0);
    const [totalEntries, setTotalEntries] = useState(0);
    const [pageChanged, setPageChanged] = useState(false);

    const [responseData, setResponseData] = useState([]);
    const [loadingCopy, setLoadingCopy] = useState(false);

    useEffect(() => {
        if (pageChanged) {
            setPageChanged(false);
            fetchPrescriptions(true);
        }
    }, [pageChanged]);

    const fetchPrescriptions = (fromPagination = false) => {
        if (!fromPagination) setRequestData({...requestData, page: 1});
        setLoading(true);
        axios.get(`${process.env.REACT_APP_API_URL2}/prescription/patient-prescriptions`, {
            headers: { "Content-Type": "application/json" },
            params: {
                amka: amka,
                company: company.id,
                ...requestData,
                page: fromPagination ? requestData.page : 1,
            }
        }).then((res) => {
            setLoading(false);
            if (res.data.status === "200") {
                setResponseData(res.data.data);
                setTotalPages(res.data.totalPages);
                setTotalEntries(res.data.totalEntries);
            } else {
                toast.error(res.data.message);
            }
        }).catch((err) => {
            setLoading(false);
            console.log(err);
            toast.error("Σφάλμα κατά την αποστολή αιτήματος.");
        })
    }

    const onPrescriptionSelect = async (barcode) => {
        try {
            setLoadingCopy(true);
            const referralResponse = await axios.get(`${process.env.REACT_APP_API_URL2}/prescription/fetch-prescription`, {
                headers: { "Content-Type": "application/json" },
                params: {
                    company: company.id,
                    barcode: barcode,
                    amka: amka,
                    fullSearch: requestData.patientsConsent,
                }
            });
            if (referralResponse.status === 200) {
                const data = referralResponse.data.data;
                let prepNewPrescription = {
                    date: new Date(),
                    type: data.type, // normal, ifet, galenical
                    repeat: data.repeat,
                    repeatPeriod: data.repeatPeriod,
                    monthlyTreatment: !!data.monthlyTreatment,
                    bimonthlyTreatment: !!data.bimonthlyTreatment,
                    chronicIllness: !!data.chronicIllness,
                    zeroParticipationReason: data.zeroParticipationReason,
                    oneDose: !!data.oneDose,
                    prescriptionWithCommercialName: !!data.prescriptionWithCommercialName,
                    prescriptionWithCommercialNameCause: data.prescriptionWithCommercialNameCause,
                    prescriptionWithCommercialNameNotes: data.prescriptionWithCommercialNameNotes,
                    medicalOpinion: !!data.medicalOpinion,
                    medicalOpinionByNumber: !!data.medicalOpinionByNumber,
                    medicalOpinionNumber: data.medicalOpinionNumber,
                    medicalOpinionIdentifier: data.medicalOpinionIdentifier,
                    medicalOpinionAMKA: data.medicalOpinionAMKA,
                    medicalOpinionDate: data.medicalOpinionDate,
                    medicalOpinionSpecialtyId: data.medicalOpinionSpecialtyId,
                    ekasLoss: data.ekasLoss,
                    homeDelivery: !!data.homeDelivery,

                    diagnoses: [],
                    drugs: [],
                    ifet: [],
                    overrides: [],
                }
                for (let diag of data.diagnoses) { // Get objects of diagnoses
                    await axios.get(`${process.env.REACT_APP_API_URL2}/prescription/icd10s`, {
                        headers: { 'Content-Type': 'application/json' },
                        params: {
                            company: company.id,
                            filter: diag.code,
                        }
                    }).then((res) => {
                        if (res.data.status === "200") {
                            const icd10Data = res.data.data;
                            const matchingICD10 = icd10Data.find((el) => el.code === diag.code);
                            if (matchingICD10) prepNewPrescription.diagnoses.push(matchingICD10);
                        }
                    })
                }
                if (prepNewPrescription.type === "normal") {
                    for (let drug of data.drugs) {
                        await axios.get(`${process.env.REACT_APP_API_URL2}/prescription/drugs`, {
                            headers: { 'Content-Type': 'application/json' },
                            params: {
                                company: company.id,
                                barcode: drug.barcode,
                            }
                        }).then((res) => {
                            if (res.data.status === "200") {
                                const drugData = res.data.data;
                                const matchingDrug = drugData.find((el) => el.barcode === drug.barcode);
                                if (matchingDrug) {
                                    prepNewPrescription.drugs.push({
                                        ...matchingDrug,
                                        erp_displayName: drug.erp_displayName,
                                        erp_drugAmount: drug.erp_drugAmount,
                                        erp_participationPercentage: drug.erp_participationPercentage,
                                        erp_doseAmount: drug.erp_doseAmount,
                                        erp_frequency: drug.erp_frequency,
                                        erp_days: drug.erp_days,
                                        erp_notes: drug.erp_notes,
                                        erp_correlated_icd10s: drug.erp_correlated_icd10s,
                                        erp_generic_cluster: drug.erp_generic_cluster,
                                    })
                                }
                            }
                        })
                    }
                } else if (prepNewPrescription.type === "ifet") {
                    prepNewPrescription.ifet = data.ifet;
                }

                prescriptionCallback(prepNewPrescription);
            }
            setLoadingCopy(false);
        } catch (error) {
            setLoadingCopy(true);
            console.log(error);
            toast.error("Σφάλμα κατά την αντιγραφή.");
        }
    }

    const handleOnChange = (e, type, name) => {
        if (type === "db") {
            if (moment(e).isValid()) {
                setRequestData({...requestData, [name] : moment(e).format("DD/MM/YYYY")});
            } else {
                setRequestData({...requestData, [name] : ""});
            }
        } else if (type === "cb") {
            setRequestData({...requestData, [name] : e.target.checked});
        }
    }

    const toFirstPage = () => {
        setRequestData({...requestData, page: 1});
        setPageChanged(true);
    }

    const toPreviousPage = () => {
        setRequestData({...requestData, page: requestData.page - 1});
        setPageChanged(true);
    }

    const toNextPage = () => {
        setRequestData({...requestData, page: requestData.page + 1});
        setPageChanged(true);
    }

    const toLastPage = () => {
        setRequestData({...requestData, page: totalPages});
        setPageChanged(true);
    }

    return (
        <React.Fragment>
            <Modal show={show} onHide={() => setShow(false)} dialogClassName={"modal65PercentWidth"}>
                <Modal.Header closeButton>
                    <Modal.Title>Αντιγραφή Συνταγής</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Row>
                        <Col md={3}>
                            <DateHelper
                                dateFromAndToFunction={(from, to) => setRequestData({...requestData, date_from: from, date_to: to})}
                            />
                        </Col>
                        <Col md={3}>
                            <DateBox
                                name={"date_from"}
                                label={"Ημερομηνία από"}
                                selected={moment(requestData.date_from, "DD/MM/YYYY").isValid() ? moment(requestData.date_from, "DD/MM/YYYY").toDate() : ""}
                                onChange={(e) => handleOnChange(e, "db", "date_from")}
                                allowOtherYears={true}
                            />
                        </Col>
                        <Col md={3}>
                            <DateBox
                                name={"date_to"}
                                label={"Ημερομηνία έως"}
                                selected={moment(requestData.date_to, "DD/MM/YYYY").isValid() ? moment(requestData.date_to, "DD/MM/YYYY").toDate() : ""}
                                onChange={(e) => handleOnChange(e, "db", "date_to")}
                                allowOtherYears={true}
                            />
                        </Col>
                        <Col md={3}>
                            <div style={{marginTop: "25px"}}>
                                <BetterCheckBox
                                    name={"patientsConsent"}
                                    text={"Έχω την συγκατάθεση του ασθενή να δω όλες τις συνταγές"}
                                    checked={requestData.patientsConsent}
                                    onChange={(e) => handleOnChange(e, "cb", "patientsConsent")}
                                />
                            </div>
                        </Col>
                    </Row>
                    <Row className={"d-flex justify-content-end"}>
                        <Col md={3} className={"d-flex justify-content-end"}>
                            <Button onClick={() => fetchPrescriptions(false)} disabled={loading} style={{marginTop: "20px"}}>
                                Αναζήτηση {loading && <Spinner className={"ml-2"} animation={"border"} variant={"dark"} />}
                            </Button>
                        </Col>
                    </Row>
                    {loadingCopy ? (
                        <div style={{textAlign: "center"}}>Γίνεται αντιγραφή παρακαλώ περιμένετε...<Spinner size={"sm"} className={"ml-2"} animation={"border"} variant={"dark"} /></div>
                    ) : responseData.length > 0 ? (
                        <Row className={"mt-2"}>
                            {totalPages > 0 && (
                                <React.Fragment>
                                    <Col md={3}>
                                        <div className={"text-muted"} style={{marginTop: "10px"}}>Σύνολο αποτελεσμάτων: {totalEntries}</div>
                                    </Col>
                                    <Col md={9}>
                                        <div className="d-flex justify-content-end mb-2">
                                            <Button
                                                size={"sm"}
                                                className={"mr-1"}
                                                onClick={() => toFirstPage()}
                                                disabled={requestData.page === 1 || loading}
                                            >
                                                {t('SalesBalanceSheet.first')}
                                            </Button>
                                            <Button
                                                size={"sm"}
                                                className={"mr-1"}
                                                onClick={() => toPreviousPage()}
                                                disabled={requestData.page === 1 || loading}
                                            >
                                                {t('SalesBalanceSheet.previous')}
                                            </Button>
                                            <Badge style={{fontSize: "12px", lineHeight: "20px"}} className="mr-1"
                                                   bg="primary">{t('SalesBalanceSheet.page')} {requestData.page} / {totalPages}</Badge>
                                            <Button
                                                size={"sm"}
                                                className={"mr-1"}
                                                onClick={() => toNextPage()}
                                                disabled={requestData.page === totalPages || loading}
                                            >
                                                {t('SalesBalanceSheet.next')}
                                            </Button>
                                            <Button
                                                size={"sm"}
                                                className={"mr-1"}
                                                onClick={() => toLastPage()}
                                                disabled={requestData.page === totalPages || loading}
                                            >
                                                {t('SalesBalanceSheet.last')}
                                            </Button>
                                        </div>
                                    </Col>
                                </React.Fragment>
                            )}
                            <Col md={12}>
                                <table className={"simpleClassicTable prescriptionTable"}>
                                    <colgroup>
                                        <col span={1} style={{width: "20%"}}></col>
                                        <col span={1} style={{width: "10%"}}></col>
                                        <col span={1} style={{width: "35%"}}></col>
                                        <col span={1} style={{width: "35%"}}></col>
                                    </colgroup>
                                    <thead>
                                    <tr>
                                        <th>Barcode</th>
                                        <th>Ημερομηνία Συντ/σης</th>
                                        <th>Διαγνώσεις</th>
                                        <th>Φάρμακα</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {responseData.map((o, idx) => (
                                        <tr key={`copyPrescr-${idx}`} onClick={() => onPrescriptionSelect(o.barcode)}>
                                            <td>{o.barcode}</td>
                                            <td>{o.issueDate}</td>
                                            <td>{textToLines(o.diagnoses)}</td>
                                            <td>{textToLines(o.drugs)}</td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                            </Col>
                        </Row>
                    ) : (
                        <React.Fragment></React.Fragment>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-primary" onClick={() => setShow(false)}>
                        Κλείσιμο
                    </Button>
                </Modal.Footer>
            </Modal>
        </React.Fragment>
    )
}

export default CopyPrescriptionModal
